import { useEffect, useState } from 'react';
import { useAgentState, type AgentState } from './agentState';

const LS_STATE = 'ccark.agentState';
const LS_STATE_AT = 'ccark.agentStateAt';

/**
 * Agent durumu için kalıcı süre sayacı.
 *
 * - Agent state değiştiğinde (kuyruk-disi → kuyrukta vb) timer sıfırlanır
 *   ve yeni timestamp localStorage'a yazılır.
 * - Sayfa refresh / yeniden açılış sonrası localStorage'tan okur → süre
 *   kaldığı yerden devam eder.
 *
 * Industry pattern: Genesys/AloTech bunu backend'de tutar (active_sessions
 * tablosu + state_changes timeline), frontend her tick'te server'dan alır
 * (WS). Bizim mevcut MVP'miz için localStorage yeterli; ileride
 * SessionsService'ten gelen sunucu zamanına bağlanacak.
 */
export function useStateTimer(enabled: boolean = true): number {
  const agentState = useAgentState();
  const [, setTick] = useState(0);
  // Frozen value while `enabled` is false. When connection drops we lock the
  // display at the elapsed-so-far value; on reconnect we shift the stored
  // `startedAt` forward by the frozen duration so the timer resumes from the
  // same number instead of jumping ahead. Industry call-centers (Genesys,
  // Five9) display "Available time" only while the agent is actually able to
  // take calls — counting through a PBX outage would inflate KPIs.
  const [frozenAt, setFrozenAt] = useState<number | null>(null);

  // State değişimini yakala, persist et
  useEffect(() => {
    const stored = localStorage.getItem(LS_STATE);
    if (stored !== agentState) {
      localStorage.setItem(LS_STATE, agentState);
      localStorage.setItem(LS_STATE_AT, String(Date.now()));
    }
  }, [agentState]);

  // Freeze / unfreeze on connection-health flips
  useEffect(() => {
    if (!enabled && frozenAt === null) {
      const elapsed = Math.max(0, Math.floor((Date.now() - readStartedAt(agentState)) / 1000));
      setFrozenAt(elapsed);
    } else if (enabled && frozenAt !== null) {
      // Shift startedAt so the next computed value equals frozenAt (continues
      // seamlessly instead of jumping forward by the disconnect duration).
      localStorage.setItem(LS_STATE_AT, String(Date.now() - frozenAt * 1000));
      setFrozenAt(null);
    }
  }, [enabled, frozenAt, agentState]);

  // 1 sn tick — sadece enabled iken; donmuşken render trigger gereksiz
  useEffect(() => {
    if (!enabled) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [enabled]);

  if (frozenAt !== null) return frozenAt;
  const startedAt = readStartedAt(agentState);
  return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
}

function readStartedAt(currentState: AgentState): number {
  const storedAt = localStorage.getItem(LS_STATE_AT);
  const storedState = localStorage.getItem(LS_STATE);
  if (storedAt && storedState === currentState) {
    const ts = parseInt(storedAt, 10);
    if (!Number.isNaN(ts)) return ts;
  }
  // State stored !== current ise henüz useEffect tetiklenmedi
  // (ilk render) — şimdiyi göster, useEffect bir sonraki tick'te düzeltir.
  return Date.now();
}

/** Logout sırasında / kullanıcı değişiminde sıfırla. */
export function clearStateTimer(): void {
  try {
    localStorage.removeItem(LS_STATE);
    localStorage.removeItem(LS_STATE_AT);
  } catch { /* */ }
}
